import { clone } from 'helpers/global.helper/global.helper'
import {
  diffBidProductionPlanChart,
  diffPreviousProductionPlanChart,
  elplanBids,
  followupExclusiveDeviationsOutcomeChart,
  followupExclusiveDeviationsOutcomeExclRatedPowerChart,
  followupForecastChart,
  followupInclusiveDeviationsOutcomeChart,
  followupInclusiveDeviationsOutcomeExklRatedPowerChart,
  followupProdOptimalChart,
  followupProdOptimalExclRatedPowerChart,
  followupProdPlanOutcomeChart,
  followupProdPlanOutcomeExclRatedPowerChart,
  followupProdTotalCostHorizontalBars,
  followupProdTotalCostHorizontalBarsWithDifferentialCost,
  followupProdTotalEnergyHorizontalBars,
  followupProdTotalEnergyHorizontalBarsWithDifferentialCalc,
  followupProductionKpi,
  marginalCostChart,
  marginalCostKpi,
  OptimizationsInfoForecastDiff,
  OptimizationsInfoOutcome,
  productionPlanChart,
  productionPlanForecastChart,
  productionPlanForecastExclElChart,
  productionPlanKpi,
  productionPlanTable,
  realTimeOptimizationBars,
  reviewAccuracyKpi,
  reviewForecastChart,
  reviewForecasts,
  sandboxForecast,
  sandboxProductionKpi,
  sandboxProductionPlan,
  sandboxProductionResult,
} from 'ui/uiConfig/template/templateUiConfigs'

type uiConfigComponentsWithDefaults =
  | 'intraday_trading_prototype_view'
  | 'production_plan_forecast'
  | 'optimize_production_plan'
  | 'followup_forecast'
  | 'production_plan_table'
  | 'sandbox_forecast'
  | 'sandbox_production_plan'
  | 'followup_production'
  | 'follow_up_prod_kpi'
  | 'marginal_cost_chart'
  | 'marginal_cost_kpi'
  | 'review_forecasts'
  | 'availability'
  | 'unit_availability'
  | 'elplan_bids'
  | 'diff_previous_production_plan'
  | 'diff_bid_production_plan'
  | 'optimizations_info_forecast_diff'
  | 'optimizations_info_outcome'

const defaultChildrenTemplates: Record<uiConfigComponentsWithDefaults, UiConfig[]> = {
  intraday_trading_prototype_view: [productionPlanKpi, productionPlanChart],
  production_plan_forecast: [productionPlanForecastChart],
  optimize_production_plan: [productionPlanKpi, productionPlanChart],
  diff_previous_production_plan: [diffPreviousProductionPlanChart],
  diff_bid_production_plan: [diffBidProductionPlanChart],
  followup_forecast: [followupForecastChart],
  production_plan_table: [productionPlanTable],
  sandbox_forecast: [],
  sandbox_production_plan: [sandboxProductionKpi, sandboxProductionPlan],
  optimizations_info_forecast_diff: [],
  optimizations_info_outcome: [],
  marginal_cost_chart: [],
  marginal_cost_kpi: [],
  follow_up_prod_kpi: [],
  review_forecasts: [reviewForecasts, reviewForecastChart, reviewAccuracyKpi],
  followup_production: [
    followupProdOptimalChart,
    followupInclusiveDeviationsOutcomeChart,
    followupProdPlanOutcomeChart,
    followupExclusiveDeviationsOutcomeChart,
    followupProdTotalEnergyHorizontalBars,
    followupProdTotalCostHorizontalBars,
  ],
  availability: [],
  unit_availability: [],
  elplan_bids: [],
}
const additionalTemplates: UiConfig[] = [
  productionPlanForecastExclElChart,
  followupProdOptimalExclRatedPowerChart,
  followupProdPlanOutcomeExclRatedPowerChart,
  followupExclusiveDeviationsOutcomeExclRatedPowerChart,
  followupInclusiveDeviationsOutcomeExklRatedPowerChart,
  followupProdTotalCostHorizontalBarsWithDifferentialCost,
  followupProdTotalEnergyHorizontalBarsWithDifferentialCalc,
]

const defaultTemplate: Record<string, UiConfig | (() => UiConfig)> = {
  intraday_trading_prototype_view: {
    display_name: '[prototype] Intraday trading prototype result',
    component: 'intraday_trading_prototype_view',
    alias: {},
    props: {
      use_default: true,
    },
    dataset_instructions: [],
  },
  sandbox_forecast: sandboxForecast,
  follow_up_prod_kpi: followupProductionKpi,
  review_forecasts: reviewForecasts,
  sandbox_production_plan: sandboxProductionResult,
  marginal_cost_chart: marginalCostChart,
  marginal_cost_kpi: marginalCostKpi,
  real_time_optimization_bars: realTimeOptimizationBars,
  review_accuracy_kpi: reviewAccuracyKpi,
  elplan_bids: elplanBids,
  optimizations_info_forecast_diff: OptimizationsInfoForecastDiff,
  optimizations_info_outcome: OptimizationsInfoOutcome,
}

const parentComponentToIds: Record<string, number[]> = {}

/**
 * Sentinel ID placed at index 0 of parentComponentToIds[component] by
 * initializeTemplates() before template UIDs are pushed. This value is used
 * to filter out the placeholder when listing real default-template children.
 */
export const TEMPLATE_SENTINEL_ID = 1

export function initializeTemplates(addUiConfig: (uiConfig: UiConfig) => void): void {
  const componentNamesWithDefaults = Object.keys(defaultChildrenTemplates) as uiConfigComponentsWithDefaults[]

  componentNamesWithDefaults.forEach((component, i) => {
    parentComponentToIds[component] = [1]

    if (!defaultChildrenTemplates[component]) {
      return
    }

    defaultChildrenTemplates[component].forEach((template, j) => {
      const uid = template.uid || -1 * ((i + 1) * 1000 + j)
      const uiConfig: UiConfig = {
        ...clone(template),
        children_ids: [],
        id: uid,
        uid: uid,
        version: 0,
        is_published: true,
      }

      parentComponentToIds[component].push(uid)
      addUiConfig(uiConfig)
    })
  })

  additionalTemplates.forEach((template, i) => {
    const uid = template.uid || -1 * (100 + i)

    const uiConfig: UiConfig = {
      ...clone(template),
      children_ids: [],
      id: uid,
      uid: uid,
      version: 0,
      is_published: true,
    }

    addUiConfig(uiConfig)
  })
}

export function getDefaultChildrenIds(parentComponent: string): number[] {
  return parentComponentToIds[parentComponent] || []
}

export function getDefaultTemplate(parentComponent: string): UiConfig | null {
  const t = defaultTemplate[parentComponent] || null

  if (typeof t === 'function') {
    return t()
  }

  return t
}

export function applyDefaultTemplate(_uiConfig: UiConfig): UiConfig {
  if (!_uiConfig.component) {
    return _uiConfig
  }

  const template = getDefaultTemplate(_uiConfig.component)

  if (!template) {
    return _uiConfig
  }

  const uiConfig = clone(_uiConfig)

  uiConfig.props = {
    ...(uiConfig.props || {}),
    ...(template.props || {}),
  }

  uiConfig.dataset_instructions = [...(uiConfig.dataset_instructions || []), ...(template.dataset_instructions || [])]

  uiConfig.alias = {
    ...(template.alias || {}),
    ...(uiConfig.alias || {}),
  }

  uiConfig.display_name = uiConfig.display_name || template.display_name || ''

  return uiConfig
}
